// "VoteBeats at your next event?" contact form: options, branching and checks (ITER-200)
// Keep in sync with functions/src/utils/nextEventContact.ts

export const NEXT_EVENT_CONTACT_SOURCES = ['event-link', 'wrap-up', 'ended-page', 'missed-it', 'shared', 'direct'] as const;
export const ROLES = ['dj', 'planner', 'group', 'curious'] as const;
export const EVENT_TYPES = ['wedding', 'birthday', 'school-dance', 'church-youth', 'other'] as const;
export const EVENT_TIMINGS = ['within-month', '1-3-months', '3-6-months', 'later', 'not-sure'] as const;
export const CROWD_SIZES = ['under-50', '50-100', '100-250', '250-plus'] as const;
export const DJ_EVENTS_PER_YEAR = ['under-10', '10-25', '25-50', '50-plus'] as const;
export const PREFERRED_CONTACTS = ['call', 'text', 'email'] as const;

export type NextEventContactSource = (typeof NEXT_EVENT_CONTACT_SOURCES)[number];
export type Role = (typeof ROLES)[number];
export type EventType = (typeof EVENT_TYPES)[number];
export type EventTiming = (typeof EVENT_TIMINGS)[number];
export type CrowdSize = (typeof CROWD_SIZES)[number];
export type DjEventsPerYear = (typeof DJ_EVENTS_PER_YEAR)[number];
export type PreferredContact = (typeof PREFERRED_CONTACTS)[number];
export type DetailField = 'eventType' | 'eventTiming' | 'crowdSize' | 'djEventsPerYear';
export type ContactStep = 'role' | 'details' | 'note' | 'contact' | 'done';

export const ROLE_LABELS: Record<Role, string> = {
  dj: "I'm a DJ and want to offer this to my clients",
  planner: "I'm planning my own event",
  group: 'I run events for a group (school, church, youth program, venue)',
  curious: 'Just curious',
};

export const ROLE_SHORT_LABELS: Record<Role, string> = {
  dj: 'DJ',
  planner: 'Planning own event',
  group: 'Runs events for a group',
  curious: 'Just curious',
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  wedding: 'Wedding',
  birthday: 'Birthday or private party',
  'school-dance': 'School dance',
  'church-youth': 'Church or youth event',
  other: 'Something else',
};

export const EVENT_TIMING_LABELS: Record<EventTiming, string> = {
  'within-month': 'Within a month',
  '1-3-months': '1–3 months',
  '3-6-months': '3–6 months',
  later: 'Later than that',
  'not-sure': 'Not sure yet',
};

export const CROWD_SIZE_LABELS: Record<CrowdSize, string> = {
  'under-50': 'Under 50',
  '50-100': '50–100',
  '100-250': '100–250',
  '250-plus': '250+',
};

export const DJ_EVENTS_PER_YEAR_LABELS: Record<DjEventsPerYear, string> = {
  'under-10': 'Under 10',
  '10-25': '10–25',
  '25-50': '25–50',
  '50-plus': '50+',
};

export const PREFERRED_CONTACT_LABELS: Record<PreferredContact, string> = {
  call: 'Call',
  text: 'Text',
  email: 'Email',
};

export const SOURCE_LABELS: Record<NextEventContactSource, string> = {
  'event-link': 'Event page link',
  'wrap-up': 'Wrap-up pop-up',
  'ended-page': 'Ended page',
  'missed-it': '"Sorry you missed this one" screen',
  shared: 'Shared link',
  direct: 'Direct visit',
};

const BRANCH_FIELDS: Record<Role, DetailField[]> = {
  dj: ['djEventsPerYear'],
  planner: ['eventType', 'eventTiming', 'crowdSize'],
  group: ['eventType', 'crowdSize'],
  curious: [],
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[0-9+()\-.\s]{7,20}$/;

export const MAX_NAME_LENGTH = 100;
export const MAX_NOTE_LENGTH = 1000;
export const MAX_EMAIL_LENGTH = 254;

export function parseContactSource(raw: string | null): NextEventContactSource {
  return (NEXT_EVENT_CONTACT_SOURCES as readonly string[]).includes(raw ?? '') ? (raw as NextEventContactSource) : 'direct';
}

export function detailFields(role: Role | null): DetailField[] {
  return role ? BRANCH_FIELDS[role] : [];
}

export function nextStep(step: ContactStep, role: Role | null): ContactStep {
  if (step === 'role') return detailFields(role).length === 0 ? 'note' : 'details';
  if (step === 'details') return 'note';
  if (step === 'note') return 'contact';
  return 'done';
}

export function isValidEmail(email: string): boolean {
  return email.length <= MAX_EMAIL_LENGTH && EMAIL_PATTERN.test(email);
}

export function isValidPhone(phone: string): boolean {
  return PHONE_PATTERN.test(phone) && phone.replace(/\D/g, '').length >= 7;
}

export interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  preferredContact: PreferredContact | null;
  note: string;
  ageConfirmed: boolean;
}

export function validateContactForm(form: ContactFormValues): string | null {
  const name = form.name.trim();
  const email = form.email.trim();
  const phone = form.phone.trim();
  if (!name) return 'Add your name.';
  if (name.length > MAX_NAME_LENGTH) return `Keep your name under ${MAX_NAME_LENGTH} characters.`;
  if (form.note.trim().length > MAX_NOTE_LENGTH) return `Keep the note under ${MAX_NOTE_LENGTH} characters.`;
  if (!email && !phone) return 'Add an email or a phone number.';
  if (email && !isValidEmail(email.toLowerCase())) return 'Check the email address.';
  if (phone && !isValidPhone(phone)) return 'Check the phone number.';
  if (form.preferredContact === 'email' && !email) return "Add the email for how you'd like us to reach you.";
  if ((form.preferredContact === 'call' || form.preferredContact === 'text') && !phone) {
    return "Add the phone number for how you'd like us to reach you.";
  }
  if (!form.ageConfirmed) return "Please confirm you're 13 or older, or a parent or guardian is filling this in.";
  return null;
}

export function contactSentKey(eventId: string | null): string {
  return `votebeats_next_event_contact_${eventId ?? 'none'}`;
}
